import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { useLazySearchPlacesQuery } from '../../../redux/api/authorized/places';
import { Label, ErrorMessage, TextInputContainer, StyledInput } from '../Text';
import CloseIcon from '../../../assets/icons/close.svg?react';

const SuggestionsContainer = styled.div`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  background-color: ${({ theme }) => theme.background.light};
  border: 1px solid ${({ theme }) => theme.text.secondary};
  border-radius: 16px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: visible;
  z-index: 10000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 4px;
`;

const SuggestionItem = styled.div`
  padding: 12px 15px;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.background.primary};
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.text.regular};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.background.primary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const SuggestionMainText = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.text.regular};
  margin-bottom: 4px;
`;

const SuggestionSecondaryText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text.secondary};
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: ${({ theme }) => theme.text.secondary};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.text.regular};
  }

  &:focus {
    outline: none;
  }
`;

const AddressFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const AddressAutocomplete = ({
  label,
  placeholder = 'Start typing an address...',
  value,
  onChange,
  onBlur,
  onFocus,
  addressLine1,
  addressLine2,
  city,
  state,
  postalCode,
  country,
  setAddressLine1,
  setAddressLine2,
  setFormattedAddress,
  setCity,
  setState,
  setPostalCode,
  setCountry,
  error,
  addressLine1Error,
  addressLine2Error,
  cityError,
  stateError,
  postalCodeError,
  countryError,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const theme = useTheme();
  const [triggerSearch, { isLoading: isSearching }] =
    useLazySearchPlacesQuery();
  const isLoading = isSearching;

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current && containerRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: inputRect.bottom + 4,
        left: containerRect.left,
        width: containerRect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (showSuggestions && suggestions.length > 0) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showSuggestions, suggestions, updateDropdownPosition]);

  const fetchSuggestions = useCallback(
    async (query) => {
      if (!query || query.length < 3) {
        setSuggestions([]);
        return;
      }

      try {
        const result = await triggerSearch(query);

        if (result.data) {
          if (
            result.data.status === 'OK' ||
            result.data.status === 'ZERO_RESULTS'
          ) {
            setSuggestions(result.data.predictions || []);
          } else {
            console.error('Places API error:', result.data.status);
            setSuggestions([]);
          }
        } else if (result.error) {
          console.error('Error fetching address suggestions:', result.error);
          setSuggestions([]);
        }
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setSuggestions([]);
      }
    },
    [triggerSearch],
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(e);

    // Debounce API calls
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (newValue.length >= 3) {
        fetchSuggestions(newValue);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleSuggestionClick = async (prediction) => {
    setInputValue(prediction.description || '');
    setShowSuggestions(false);
    setSuggestions([]);

    // Populate all address fields
    // Check if addressLine2 exists in the response (might be null, undefined, or empty string)
    // Check multiple possible field names from the API
    const addressLine2Value =
      prediction.addressLine2 ??
      prediction.address_line_2 ??
      prediction.addressLine2Value ??
      prediction.secondaryAddress ??
      prediction.subpremise ?? // Google Places API uses 'subpremise' for apt/suite
      prediction.sublocality ??
      '';

    setAddressLine1?.(prediction.addressLine1 || prediction.address_line_1 || '');
    // Always set addressLine2, even if empty - this ensures the field shows up
    setAddressLine2?.(addressLine2Value || '');
    setCity?.(prediction.city || '');
    setFormattedAddress?.(prediction.formattedAddress || prediction.formatted_address || '');
    setState?.(prediction.state || '');
    setPostalCode?.(prediction.postalCode || prediction.postal_code || '');
    setCountry?.(prediction.country || '');
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (inputValue.length >= 3 && suggestions.length > 0) {
      setShowSuggestions(true);
      updateDropdownPosition();
    }
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      setIsFocused(false);
      onBlur?.(e);
    }, 200);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setAddressLine1?.('');
    setAddressLine2?.('');
    setCity?.('');
    setFormattedAddress?.('');
    setState?.('');
    setPostalCode?.('');
    setCountry?.('');
    // Trigger onChange with empty value
    if (onChange) {
      const syntheticEvent = {
        target: { value: '' },
      };
      onChange(syntheticEvent);
    }
    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const hasAddressData =
    addressLine1 || city || state || postalCode || country;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        padding: '0 1px',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 1,
      }}
    >
      {label && <Label>{label}</Label>}
      <TextInputContainer $isFocused={isFocused} $hasError={!!error}>
        <StyledInput
          ref={inputRef}
          placeholder={placeholder}
          type='text'
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...rest}
        />
        {hasAddressData && (
          <ClearButton
            type='button'
            onClick={handleClear}
            aria-label='Clear address'
          >
            <CloseIcon
              width={16}
              height={16}
              style={{ color: theme.text.secondary }}
            />
          </ClearButton>
        )}
      </TextInputContainer>
      {showSuggestions && suggestions.length > 0 && (
        <SuggestionsContainer
          $top={dropdownPosition.top}
          $left={dropdownPosition.left}
          $width={dropdownPosition.width}
        >
          {suggestions.map((prediction) => (
            <SuggestionItem
              key={prediction.place_id}
              onClick={() => handleSuggestionClick(prediction)}
            >
              <SuggestionMainText>
                {prediction.formattedAddress || prediction.description}
              </SuggestionMainText>
            </SuggestionItem>
          ))}
        </SuggestionsContainer>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {hasAddressData && (
        <AddressFieldsContainer>
          <TextInputContainer
            $isFocused={false}
            $hasError={!!addressLine1Error}
          >
            <StyledInput
              placeholder='Address Line 1'
              type='text'
              value={addressLine1 || ''}
              onChange={(e) => setAddressLine1?.(e.target.value)}
            />
          </TextInputContainer>
          {addressLine1Error && (
            <ErrorMessage>{addressLine1Error}</ErrorMessage>
          )}

          <TextInputContainer
            $isFocused={false}
            $hasError={!!addressLine2Error}
          >
            <StyledInput
              placeholder='Address Line 2 (Apt, Suite, etc.)'
              type='text'
              value={addressLine2 || ''}
              onChange={(e) => setAddressLine2?.(e.target.value)}
            />
          </TextInputContainer>
          {addressLine2Error && (
            <ErrorMessage>{addressLine2Error}</ErrorMessage>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: '2' }}>
              <TextInputContainer
                $isFocused={false}
                $hasError={!!cityError}
              >
                <StyledInput
                  placeholder='City'
                  type='text'
                  value={city || ''}
                  onChange={(e) => setCity?.(e.target.value)}
                />
              </TextInputContainer>
              {cityError && <ErrorMessage>{cityError}</ErrorMessage>}
            </div>

            <div style={{ flex: '1' }}>
              <TextInputContainer
                $isFocused={false}
                $hasError={!!stateError}
              >
                <StyledInput
                  placeholder='State'
                  type='text'
                  value={state || ''}
                  onChange={(e) => setState?.(e.target.value)}
                />
              </TextInputContainer>
              {stateError && <ErrorMessage>{stateError}</ErrorMessage>}
            </div>

            <div style={{ flex: '1' }}>
              <TextInputContainer
                $isFocused={false}
                $hasError={!!postalCodeError}
              >
                <StyledInput
                  placeholder='ZIP Code'
                  type='text'
                  value={postalCode || ''}
                  onChange={(e) => setPostalCode?.(e.target.value)}
                />
              </TextInputContainer>
              {postalCodeError && (
                <ErrorMessage>{postalCodeError}</ErrorMessage>
              )}
            </div>
          </div>

          <TextInputContainer
            $isFocused={false}
            $hasError={!!countryError}
          >
            <StyledInput
              placeholder='Country'
              type='text'
              value={country || ''}
              onChange={(e) => setCountry?.(e.target.value)}
            />
          </TextInputContainer>
          {countryError && <ErrorMessage>{countryError}</ErrorMessage>}
        </AddressFieldsContainer>
      )}
    </div>
  );
};

export default AddressAutocomplete;
